import { z } from 'zod';

/**
 * Automatic price discounts — standing rules the shop sets once that reprice the
 * catalogue everywhere at once (storefront, app, POS, mandob, sales documents).
 *
 * Distinct from a [[Coupon]], which is a code the buyer types at checkout against
 * an order total. A discount is already showing on the shelf edge before anyone
 * asks for it, so it is resolved wherever a price is produced, not at tender time.
 *
 * `scope` + `targetId` say what the rule covers: one product, a whole brand, a
 * whole category (including its descendants), or the entire catalogue. Rules never
 * stack — the most specific matching rule wins, product > brand > category > all.
 *
 * `appliesTo` is the مفرد / جملة axis. A line is WHOLESALE when it sells a
 * packaging unit (carton / pack / dozen — an `item_units` row) and RETAIL when it
 * sells the item's own base unit. That is a property of the line, so an anonymous
 * till sale and a signed-in shopper are judged the same way.
 *
 * `audience` is the CUSTOMER axis, orthogonal to both of the above: "20% off
 * brand X, for ماركيت customers only" has to be expressible. It reuses the price
 * list a customer is already assigned to, because that is how the shop already
 * thinks of its customer types (المكاتب / ماركيت / قرطاسية) — and because the
 * same list already decides that customer's prices.
 */
const decimalString = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative decimal');
const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);

export const DiscountScopeSchema = z.enum(['product', 'brand', 'category', 'all']);
export type DiscountScope = z.infer<typeof DiscountScopeSchema>;

export const DiscountTypeSchema = z.enum(['percent', 'fixed']);
export type DiscountType = z.infer<typeof DiscountTypeSchema>;

export const DiscountAppliesToSchema = z.enum(['retail', 'wholesale', 'both']);
export type DiscountAppliesTo = z.infer<typeof DiscountAppliesToSchema>;

/**
 * WHO a rule is for. Three MODES, not a set of ids: "everyone" and "customers on
 * no list at all" are not lists, so a bare array could never express them — an
 * empty one would be indistinguishable from "everyone". A discriminator is
 * unavoidable, and it matches this schema's own idiom, where `scope` already says
 * what `targetId` means.
 *
 * `audiencePriceListIds` is meaningful only for 'price_list'.
 */
export const DiscountAudienceSchema = z.enum(['all', 'price_list', 'no_list']);
export type DiscountAudience = z.infer<typeof DiscountAudienceSchema>;

export const DiscountSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(200),
  scope: DiscountScopeSchema,
  /** Item / brand / category id. Null exactly when `scope` is 'all'. */
  targetId: z.string().uuid().nullable(),
  /** Resolved label for the target, for display in lists (server-filled). */
  targetName: z.string().nullable(),
  discountType: DiscountTypeSchema,
  discountValue: decimalString,
  /** Denomination of a `fixed` discount — it only applies to a line priced in the same currency. Null for percent. */
  currencyCode: z.string().length(3).nullable(),
  appliesTo: DiscountAppliesToSchema,
  audience: DiscountAudienceSchema,
  /** Customer types (price lists) a 'price_list' rule names. Empty for every other audience. */
  audiencePriceListIds: z.array(z.string().uuid()),
  /** Their resolved labels, positionally aligned with the ids (server-filled, like targetName). */
  audiencePriceListNames: z.array(z.string()),
  /** Line quantity required before the rule bites; null = no minimum. */
  minQty: decimalString.nullable(),
  startsAt: z.string().datetime().nullable(),
  endsAt: z.string().datetime().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Discount = z.infer<typeof DiscountSchema>;

const discountInputShape = {
  name: z.string().min(1).max(200),
  scope: DiscountScopeSchema,
  targetId: z.string().uuid().nullable().optional(),
  discountType: DiscountTypeSchema,
  discountValue: decimalString,
  currencyCode: currencyCode.nullable().optional(),
  appliesTo: DiscountAppliesToSchema.optional(),
  audience: DiscountAudienceSchema.optional(),
  audiencePriceListIds: z.array(z.string().uuid()).optional(),
  minQty: decimalString.nullable().optional(),
  startsAt: z.string().nullable().optional(),
  endsAt: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
};

/** Shared by create and update: only ever inspects fields that are present. */
function checkPresent(v: Partial<DiscountCreate>, ctx: z.RefinementCtx): void {
  if (v.scope === 'all' && v.targetId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['targetId'], message: 'a catalogue-wide discount takes no target' });
  }
  if (v.discountType === 'percent' && v.discountValue != null && Number(v.discountValue) > 100) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['discountValue'], message: 'a percentage cannot exceed 100' });
  }
  if (v.startsAt && v.endsAt && new Date(v.startsAt) > new Date(v.endsAt)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endsAt'], message: 'the end date is before the start date' });
  }
  if (v.audience !== undefined && v.audience !== 'price_list' && v.audiencePriceListIds?.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['audiencePriceListIds'], message: 'only a customer-type discount names customer types' });
  }
}

/**
 * `all` must carry no target and every other scope must carry one — otherwise a
 * rule either silently covers the whole catalogue or matches nothing at all.
 * A percent must be ≤ 100; a fixed amount needs the currency it is denominated in.
 */
export const DiscountCreateSchema = z.object(discountInputShape).superRefine((v, ctx) => {
  checkPresent(v, ctx);
  if (v.scope !== 'all' && !v.targetId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['targetId'], message: 'pick what this discount applies to' });
  }
  if (v.discountType === 'fixed' && !v.currencyCode) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['currencyCode'], message: 'a fixed discount needs a currency' });
  }
  // A customer-type rule naming no customer type matches nobody while still
  // looking live in the list — the same trap as a scoped rule with no target.
  if (v.audience === 'price_list' && !v.audiencePriceListIds?.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['audiencePriceListIds'], message: 'pick at least one customer type' });
  }
});
export type DiscountCreate = z.infer<typeof DiscountCreateSchema>;

/**
 * A PATCH validates only what it actually carries. The create-time "pick what
 * this discount applies to" check must NOT run here: a body of `{isActive:false}`
 * says nothing about scope, and rejecting it would make the list's deactivate
 * button — and every other single-field edit — fail with a message about a field
 * the operator never touched. A required pairing is only re-checked when one half
 * of it is actually being changed.
 */
export const DiscountUpdateSchema = z.object(discountInputShape).partial().superRefine((v, ctx) => {
  checkPresent(v, ctx);
  if (v.scope !== undefined && v.scope !== 'all' && v.targetId === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['targetId'], message: 'pick what this discount applies to' });
  }
  if (v.discountType === 'fixed' && v.currencyCode === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['currencyCode'], message: 'a fixed discount needs a currency' });
  }
  // `{audience:'price_list'}` ALONE is legal here and keeps whatever customer
  // types the rule already names (the service falls back to the stored set). Only
  // an explicitly EMPTIED set is rejected, since that one is unambiguous.
  if (v.audience === 'price_list' && v.audiencePriceListIds !== undefined && v.audiencePriceListIds.length === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['audiencePriceListIds'], message: 'pick at least one customer type' });
  }
});
export type DiscountUpdate = z.infer<typeof DiscountUpdateSchema>;

/**
 * What a resolved discount did to one price. Attached to every priced line the
 * server hands out so a client can render "was 10,000 / now 8,500" without
 * recomputing anything — and, critically, without being trusted to.
 */
export const AppliedDiscountSchema = z.object({
  discountId: z.string().uuid(),
  name: z.string(),
  scope: DiscountScopeSchema,
  discountType: DiscountTypeSchema,
  discountValue: z.string(),
  /** Unit price before the discount. */
  originalUnitPrice: z.string(),
  /** Money taken off ONE unit, in the line's currency. */
  amountPerUnit: z.string(),
  /** Whole percent off, rounded, purely for a "-15%" badge. */
  percentOff: z.number(),
});
export type AppliedDiscount = z.infer<typeof AppliedDiscountSchema>;
